/**
 * Kernel & Bot API Service
 * Calls raisy_BE endpoints for kernel/bot management.
 *
 * Versioning pattern (shared by kernels, bots, and processes):
 *   - Entities are identified by a stable KEY (kernelKey / botKey / processKey).
 *   - Each save creates a NEW VERSION (immutable insert, versionNumber auto-increments).
 *   - "Edit" = create new version. Old versions preserved for audit trail.
 *   - "Delete" = archive (soft-delete via status='archived'). No hard deletes.
 *   - GET list/registry returns only the LATEST ACTIVE version per key.
 *   - GET versions/{key} returns full version history.
 */

import AuthUtils from "@/providers/firebase/authUtils";
import { type VersionLineageSeed } from "@/services/api/spanFeedbackApi";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";

// ── Types ──────────────────────────────────────────────────────────

export interface KernelSaveRequest {
  /** Pass kernelKey to create new version of existing kernel */
  kernelKey?: string;
  /** BD (Bot Dimension) number for new kernel — BE auto-assigns kernelNumber + kernelKey */
  botDimension?: number;
  /** Optional subcategory value within BD, e.g. 1, 2, 3 */
  botDimensionSub?: number;
  /** Primary KD value (KD.10) per kernel_index_dimensions.json, e.g. 1=web_search */
  kernelDimension?: number;
  /**
   * Manual KD classification: map of KD code → chosen value within this kernel's BD
   * slot, e.g. { "10": 1, "20": 2, "50": 1 }. Validated against kernel_index_dimensions;
   * the BE derives kernelDimension, tags, and classification from it.
   */
  kernelDimensionAnswers?: Record<string, number>;
  content: string;
  kernelName?: string;
  description?: string;
  kernelFormat?: "text" | "json" | "markdown";
  tags?: string[];
  inputVariables?: { name: string; description: string }[];
}

/** Where a BD155 skill's implementation comes from. See KernelData.runtimeType. */
export type SkillRuntimeType =
  | "vendored"
  | "generated"
  | "generated_on_demand"
  | "instruction"
  | "external_http"
  | "n8n"
  | "unknown";

export interface KernelData {
  kernelId: string;
  kernelKey: string;
  kernelNumber: number | null;
  kernelName: string;
  /** Full SKILL.md text — present when summary=false, absent in list (summary=true) responses. */
  content?: string;
  /** Parsed frontmatter fields — present in summary list responses instead of full content. */
  frontmatter?: Record<string, string>;
  versionNumber: number;
  contentTokenCount: number;
  kernelFormat: string;
  botDimension: number | null;
  botDimensionSub: number | null;
  /** Primary KD value (KD.10) per kernel_index_dimensions.json */
  kernelDimension: number | null;
  description: string;
  tags: string[];
  inputVariables: { name: string; type: string; required: boolean; description: string; secret?: boolean; default?: unknown }[];
  status: "active" | "archived";
  updatedAt: string;
  archivedAt: string | null;
  /** Where this skill's code comes from (PROJECT-098 T0212). Present only for BD155
   *  kernels — a BD0 schema or BD1 purpose kernel has no runtime, so the field is
   *  ABSENT rather than null for them.
   *
   *  "vendored"            code shipped with the skill and sits on disk
   *  "generated"           Raisy LLM-generated the code; a stored artifact proves it
   *  "generated_on_demand" no code yet — the FIRST run generates and executes it
   *  "instruction"         the skill IS its instructions (SKILL.md + references, no
   *                        `scripts/`). Applying them is the skill working — nothing is
   *                        LLM-written, and it must not read as "no code yet"
   *  "external_http"       provider-backed (note: dispatch not currently wired)
   *  "n8n"                 reserved; no n8n dispatch exists on the skill path yet
   *  "unknown"             the probe failed — never conflated with "no code" */
  runtimeType?: SkillRuntimeType;
  /** Review verdict for THIS kernel version (approve/reject) — tells you whether
   * this version passed review. Null/absent when never reviewed. */
  reviewStatus?: KernelReviewStatus | string | null;
  reviewFeedback?: string | null;
  reviewedBy?: string | null;
  reviewedAt?: string | null;
}

export type KernelReviewStatus = "approved" | "rejected";

export interface KernelVersionSummary {
  kernelId: string;
  versionNumber: number;
  contentTokenCount: number;
  updatedAt: string;
  /** Review verdict for this version, if any. */
  reviewStatus?: KernelReviewStatus | string | null;
  /**
   * TJ05b — is this the version the bot actually runs?
   *
   * Exactly one version per key is active. That used to be the highest-numbered one, so it
   * never needed saying; a revert now makes an EARLIER version current, and the picker would
   * otherwise show v25 selected with v26–v32 listed above it and nothing explaining why.
   */
  isActive?: boolean;
  /** T0224: why this version exists — the reason the patch writer recorded. */
  description?: string | null;
  /** T0224: the diagnose→patch run that produced this version, parsed server-side from
   *  the description's `[run=<id>]` stamp. Null for hand-made and pre-T0224 versions. */
  patchRunId?: string | null;
}

export interface BotData {
  botId: string;
  botKey: string;
  botNumber: number;
  botName: string;
  versionNumber: number;
  mode: string;
  status: string;
  kernelCount: number;
  kernel_keys_ref: string[];
  classification?: string;
  processDimension?: number;
  processDimensionSub?: number;
  /** BD0 input JSON schema — declares the bot's typed input parameters (name/type/required). */
  inputSchema?: Record<string, unknown> | null;
  /** BD999 output JSON schema. */
  outputSchema?: Record<string, unknown> | null;
  /** T0120: BD155 skill slot(s) unresolved at construct time — non-empty means the bot
   *  was built without them and RunnerService blocks invocation until resolved. */
  pendingSkills?: PendingSkill[] | null;
  createdAt: string;
}

/** T0120: a BD155 slot that couldn't be resolved when the bot was constructed. */
export interface PendingSkill {
  botDimensionSub: number | null;
  category: string;
  devRequestId: string | null;
  intent: string;
  message: string;
  /** T0201: "not_found" (nothing exists anywhere) vs "pending_approval" (candidates
   *  found but held for review). Different fix, so they must read differently. */
  reason?: "not_found" | "pending_approval" | string;
}

/** Where a missing skill sits in its lifecycle. Derived by the BE from the slot's
 *  dev_requests row plus whether a BD155 kernel under that sub actually exists yet. */
export type SkillStage =
  | "requested"          // dev request open, nobody has picked it up
  | "awaiting_approval"  // remote candidates found, held for human review (T0201)
  | "in_development"     // dev request in_progress
  | "ready"              // attachable now
  | "rejected"           // dev request rejected — this slot will not be filled
  | "untracked";         // no dev request row (its creation failed at construct time)

/** One unresolved BD155 slot, joined to its dev request. From GET /bot/{id}/skill-status. */
export interface SkillSlotStatus {
  botDimensionSub: number | null;
  category: string | null;
  intent: string | null;
  message: string | null;
  reason: string;
  stage: SkillStage;
  readyToAttach: boolean;
  /** Which signal made it ready: "kernel" (one exists) or "dev_request" (a human said so).
   *  Kept separate because only the first is verifiable. */
  readySource: "kernel" | "dev_request" | null;
  candidateKernelKey: string | null;
  candidateKernelName: string | null;
  /** Whether the candidate has runnable code today (scripts/main.py on disk or a stored
   *  artifact). A BD155 kernel is the skill's *spec*; false means the implementation gets
   *  LLM-generated on first run. Not a blocker — a distinction worth seeing before binding. */
  candidateHasImplementation: boolean;
  /** Where the candidate's code CAME FROM, as recorded on its kernel tags — the same class the
   *  catalog badge renders (`hermes` = first-party vendored, `clawhub` = third-party registry).
   *
   *  null means NO class was recorded, which covers two cases that must read identically:
   *  a kernel from a path that records no source (most of them), and a slot with no candidate
   *  at all — provenance belongs to an artefact, and there isn't one yet. Render nothing for
   *  null; substituting a default asserts a provenance nobody recorded (T0410/D-098-021). */
  candidateSourceClass: string | null;
  devRequestId: string | null;
  devRequestStatus: DevRequestStatus | null;
  devRequestNotes: string | null;
  requestedAt: string | null;
  updatedAt: string | null;
  resolvedAt: string | null;
}

export interface BotVersionSummary {
  botId: string;
  botKey: string;
  versionNumber: number;
  status: string;
  botName: string;
  mode: string;
  kernelCount: number;
  kernel_keys_ref: string[];
  createdAt: string;
  archivedAt?: string | null;
}

/** Mode tiers, mirroring MODE_PRESETS in raisy_BE/db/raisy_1000_runner.py — that block
 *  is the single source of truth for the tier table (PROJECT-081, revised 2026-08-07);
 *  this is a restatement.
 *  fast = gemini-2.5-flash-lite @ 0.1 | medium = gemini-2.5-flash @ 0.7
 *  quality = gemini-3.1-pro @ 0.7
 *  Unspecified means `fast`: medium and pro are both opt-in, never inherited. Keep these
 *  three in sync with the backend — a value the BE doesn't know is not rejected, it
 *  silently falls back to the default model. */
export type BotMode = "fast" | "medium" | "quality";

export interface BotCreateRequest {
  botName: string;
  kernel_keys_ref: string[];
  mode: BotMode;
  classification?: string;
  processDimension?: number;
  processDimensionSub?: number;
}

export interface BotEditRequest {
  botKey: string;
  botName?: string;
  kernel_keys_ref?: string[];
  mode?: string;
  classification?: string;
  processDimension?: number;
  processDimensionSub?: number;
}

export interface ApiResponse<T = Record<string, unknown>> {
  status: boolean;
  statusCode: number;
  message: string;
  data: T | null;
}

// ── Helper ─────────────────────────────────────────────────────────

async function apiCall<T>(
  path: string,
  options: RequestInit = {},
): Promise<ApiResponse<T>> {
  const headers = await AuthUtils.getAuthHeaders();
  const isFormData = options.body instanceof FormData;

  // Don't set Content-Type for multipart uploads — the browser sets the boundary.
  if (isFormData) {
    delete (headers as Record<string, string>)["Content-Type"];
  }

  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: { ...headers, ...options.headers },
  });
  return res.json();
}

// ── Config / dimension index endpoints ─────────────────────────────

/**
 * Fetch the bot dimension index (`bot_index_dimensions`) fresh from the DB-backed
 * config, so dimension names/codes always reflect the current source of truth.
 * The `data` payload is `{ bot_index_dimensions: <index JSON> }`.
 */
export async function getBotIndexDimensions(): Promise<
  ApiResponse<{ bot_index_dimensions?: unknown }>
> {
  return apiCall("/api/v1/config/indexes/bot_index_dimensions");
}

// ── Kernel Endpoints ───────────────────────────────────────────────

export interface ListKernelsParams {
  summary?: boolean;
  page?: number;
  /** Items per page. Use 1000 to load all for selector dropdowns. */
  pageSize?: number;
  sortBy?: "kernelNumber" | "kernelName" | "updatedAt" | "botDimension";
  sortOrder?: "asc" | "desc";
  /** Case-insensitive contains filter on kernelName and kernelKey. */
  search?: string;
  botDimension?: number;
  status?: "active" | "archived" | "all";
  /** PROJECT-098 T0411: source class, matched against the `source:<class>` kernel tag
   *  (e.g. "hermes"). Omit for every source. A FILTER, not a sort key: provenance is a
   *  class you narrow to, not an order you sort by (D-098-006). */
  source?: string;
  /** The sub-category inside `botDimension` (`botdimensionsub`). The second half of a kernel's
   *  address — `botDimension` alone can never express "only the Strategy documents that use a
   *  Storytelling Hook". Filtered in SQL (2026-10-12). */
  botDimensionSub?: number;
  /** Narrow to one kernel format (`markdown` | `json` | `text`). A document listing wants
   *  `markdown` and nothing else, so the estate's non-document rows are never read. */
  kernelFormat?: string;
  /** ⚠️ SCOPE TO THE CALLER'S OWN ENTITIES. A surface whose MEANING is "my documents" sets this;
   *  a surface whose meaning is "the catalogue" (Skill Catalogue, kernel assembly) does NOT, because
   *  shared/global kernels are catalogued assets there. When set, an admin or a service token is
   *  unrestricted and every other caller sees only kernels in entities he can read. */
  entitledOnly?: boolean;
  /** ⚠️ ASK TO BE ANSWERED AS A REGULAR USER, even as an admin — this is the app's own admin/user
   *  MODE (`TopBar`'s switch, `localStorage['raisy_admin_mode']`) reaching the query. Without it the
   *  switch is inert on this surface, and previewing user mode previews the ADMIN's documents, which
   *  is the opposite of what the switch promises (`TopBar.tsx:91`). Meaningful only with
   *  `entitledOnly`. */
  asRegularUser?: boolean;
  /** Include the category/sub-category breakdown of the WHOLE filtered set, computed by one
   *  grouped query. A filter dropdown needs the complete list even when only 50 rows are on
   *  screen — which is why the alternative was fetching every row. */
  facets?: boolean;
}

export interface PagedResponse<T> {
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  data: T;
}

export async function listKernels(
  params?: ListKernelsParams,
): Promise<
  ApiResponse<{
    kernels: KernelData[];
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
    /** Present only when `facets: true` was asked for. `value` is a BD code as a string, or
     *  `"none"` for the kernels that carry no dimension at all. */
    facets?: {
      categories: { value: string; count: number }[];
      subcategories: { value: string; count: number }[];
    };
  }>
> {
  const body: Record<string, unknown> = {
    summary: params?.summary ?? true,
    page: params?.page ?? 1,
    pageSize: params?.pageSize ?? 20,
    sortBy: params?.sortBy ?? "kernelNumber",
    sortOrder: params?.sortOrder ?? "asc",
    search: params?.search ?? "",
    status: params?.status ?? "active",
  };
  if (params?.source) body.source = params.source;
  if (params?.botDimension !== undefined) body.botDimension = params.botDimension;
  if (params?.botDimensionSub !== undefined) body.botDimensionSub = params.botDimensionSub;
  if (params?.kernelFormat) body.kernelFormat = params.kernelFormat;
  if (params?.entitledOnly) body.entitledOnly = true;
  if (params?.asRegularUser) body.asRegularUser = true;
  if (params?.facets) body.facets = true;
  return apiCall("/api/v1/kernel/list", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export interface SearchKernelsParams {
  query: string;
  page?: number;
  pageSize?: number;
  sortBy?: "kernelNumber" | "kernelName" | "updatedAt" | "botDimension";
  sortOrder?: "asc" | "desc";
  botDimension?: number;
  status?: "active" | "archived" | "all";
  /** PROJECT-098 T0411: same source-class filter as `listKernels`. */
  source?: string;
}

/** Search kernels across name, key, description, and tags. */
export async function searchKernels(
  params: SearchKernelsParams,
): Promise<ApiResponse<{ kernels: KernelData[]; total: number; page: number; pageSize: number; totalPages: number }>> {
  const body: Record<string, unknown> = {
    query: params.query,
    page: params.page ?? 1,
    pageSize: params.pageSize ?? 20,
    sortBy: params.sortBy ?? "kernelNumber",
    sortOrder: params.sortOrder ?? "asc",
    status: params.status ?? "active",
  };
  if (params.source) body.source = params.source;
  if (params.botDimension !== undefined) body.botDimension = params.botDimension;
  return apiCall("/api/v1/kernel/search", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function saveKernel(
  data: KernelSaveRequest,
): Promise<ApiResponse> {
  return apiCall("/api/v1/kernel/save", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function getKernel(
  kernelKey: string,
  version?: number,
  entityId?: string,
): Promise<ApiResponse<KernelData>> {
  return apiCall("/api/v1/kernel/get", {
    method: "POST",
    body: JSON.stringify({ kernelKey, version, entity_id: entityId }),
  });
}

export async function getKernelVersions(
  kernelKey: string,
): Promise<
  ApiResponse<{
    kernelKey: string;
    versionCount: number;
    versions: KernelVersionSummary[];
  }>
> {
  return apiCall(`/api/v1/kernel/versions/${encodeURIComponent(kernelKey)}`);
}

export interface KernelReviewResult {
  kernelKey: string;
  kernelId: string;
  versionNumber: number;
  reviewStatus: KernelReviewStatus;
  reviewFeedback: string | null;
  reviewedBy: string | null;
  reviewedAt: string;
}

/**
 * Record a review verdict (approve / reject) on a kernel version. Defaults to the
 * latest active version.
 */
export async function reviewKernel(
  kernelKey: string,
  verdict: KernelReviewStatus,
  feedback?: string,
  version?: number,
): Promise<ApiResponse<KernelReviewResult>> {
  return apiCall(`/api/v1/kernel/${encodeURIComponent(kernelKey)}/review`, {
    method: "POST",
    body: JSON.stringify({ verdict, feedback, version }),
  });
}

/** Archive a kernel (tags all versions as 'archived'). */
export async function archiveKernel(
  kernelKey: string,
): Promise<ApiResponse> {
  return apiCall(`/api/v1/kernel/${encodeURIComponent(kernelKey)}/archive`, {
    method: "POST",
  });
}

// ── Kernel KD signature (manual schema + automatic classify) ───────

export interface KdDimension {
  /** KD code without the family prefix, e.g. "1", "910". */
  code: string;
  /** Full code, e.g. "KD155.1". */
  fullCode: string;
  name: string;
  question: string;
  required: boolean;
  /** True for the extension dim matching the kernel's own BDsub — maps to kernelDimension
   * (the fluent third segment, e.g. the 2 in 155.9.2). Only meaningful when botDimensionSub
   * was supplied to getKdSchema(); otherwise this marks nothing (no bdsub context yet). */
  isPrimary: boolean;
  /** True for a KD dim that extends a SPECIFIC BDsub (kernel_index_dimensions v2
   * "extension_of"). A kernel anchored at a DIFFERENT BDsub can never answer this one. */
  isExtension: boolean;
  /** The BD anchor this dim extends, e.g. "BD155.9" — set only when isExtension is true. */
  extensionOf: string | null;
  /** True for structural I/O slots (900/901/999) the user should not classify. */
  isStructural: boolean;
  /** False when this dim is an extension of a BDsub OTHER than the one being classified —
   * render it disabled/hidden, never let the user answer it. Always true when no
   * botDimensionSub was supplied (no bdsub context to filter against). */
  applicable: boolean;
  /** value-number → human label. */
  values: Record<string, string>;
}

export interface KdSchema {
  botDimension: number;
  /** Echoes the botDimensionSub passed to getKdSchema, or null if omitted. */
  botDimensionSub: number | null;
  hasKdFamily: boolean;
  /** Code of the extension dim applicable to botDimensionSub, or null (no bdsub context,
   * or this BD/BDsub has no extension dim of its own — e.g. BD110/BD160 have none). */
  primaryCode: string | null;
  dimensions: KdDimension[];
}

/** Fetch the KD question set for a BD slot (drives the manual KD sub-form).
 * Pass botDimensionSub to narrow extension dims to the ones this specific BDsub can
 * answer (kernel_index_dimensions v2) — omit it to get every dim in the BD family. */
export async function getKdSchema(
  botDimension: number,
  botDimensionSub?: number,
): Promise<ApiResponse<KdSchema>> {
  const params = new URLSearchParams({ botDimension: String(botDimension) });
  if (botDimensionSub != null) params.set("botDimensionSub", String(botDimensionSub));
  return apiCall(`/api/v1/kernel/kd-schema?${params.toString()}`);
}

export interface DeriveKernelSignatureRequest {
  content: string;
  /** Omit to let the BE derive the BD anchor from content (content-only path). */
  botDimension?: number;
  botDimensionSub?: number;
  kernelKey?: string;
  kernelName?: string;
  description?: string;
  /** Also derive Details (name, description, tags, format) from content. */
  includeMetadata?: boolean;
}

export interface KernelMetadata {
  kernelFormat: "text" | "json" | "markdown";
  kernelName: string | null;
  description: string | null;
  tags: string[];
}

export interface KernelSignature {
  botDimension: number;
  botDimensionSub: number | null;
  /** Primary KD value (KD.10), null when the BD has no KD family. */
  kernelDimension: number | null;
  /** Secondary KD codes, e.g. ["KD155.20.2", "KD155.50.1"]. */
  tags: string[];
  /** Full signature string, e.g. "BD155.1_KD155.10.1_KD155.20.2". */
  classification: string;
  /** Raw KD answers {code: value} — feed back as kernelDimensionAnswers on save. */
  answers: Record<string, number>;
  /** Model that classified, or null if anchor-only / no LLM used. */
  classifiedBy: string | null;
  /** Present when includeMetadata was requested — auto-fills the Details section. */
  metadata?: KernelMetadata;
}

/**
 * Derive a kernel's signature for preview. Returns BD + KD + classification.
 * Does NOT persist — save with saveKernel({ ...content, kernelDimensionAnswers: answers }).
 */
export async function deriveKernelSignature(
  data: DeriveKernelSignatureRequest,
): Promise<ApiResponse<KernelSignature>> {
  return apiCall("/api/v1/kernel/intent_2_signature", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export interface KernelAutoCreateRequest {
  content: string;
  /** Pin the BD anchor; omit to let the BE derive it from content. */
  botDimension?: number;
  botDimensionSub?: number;
  /** Override fields; anything omitted is derived/detected from content. */
  kernelName?: string;
  description?: string;
  kernelFormat?: "text" | "json" | "markdown";
  tags?: string[];
  inputVariables?: { name: string; description: string }[];
  entity_id?: string;
  scope?: "global" | "private";
  model?: string;
}

/** What auto-create returns: the saved kernel plus the derived signature. */
export interface KernelAutoCreateResult {
  kernelId: string;
  kernelKey: string;
  kernelNumber: number;
  versionNumber: number;
  botDimension: number | null;
  botDimensionSub: number | null;
  kernelDimension: number | null;
  contentTokenCount: number;
  createdAt: string;
  classification: string | null;
  classifiedBy: string | null;
  answers: Record<string, number>;
  autoCreated: boolean;
}

/**
 * One-shot automatic kernel creation: content → derive BD anchor + KD signature +
 * Details → save. The kernel analogue of constructBot. No separate review/save step.
 */
export async function autoCreateKernel(
  data: KernelAutoCreateRequest,
): Promise<ApiResponse<KernelAutoCreateResult>> {
  return apiCall("/api/v1/kernel/intent_2_kernel", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

// ── PROJECT-031: Kernel Semantic Search & Exact Match ──────────────

export interface KernelSearchResult {
  kernel: KernelData;
  score: number;
}

export interface KernelSearchRequest {
  query: string;
  botDimension?: number;
  top_n?: number;
  threshold?: number;
}

export interface KernelMatchRequest {
  botDimension: number;
  botDimensionSub: number;
  kernelDimension?: number;
}

/** Semantic search over kernels using pgvector cosine similarity. Admin-only. */
export async function semSearchKernels(
  data: KernelSearchRequest,
): Promise<ApiResponse<{ query: string; results: KernelSearchResult[] }>> {
  return apiCall("/api/v1/kernel/sem_search", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

/** Exact-match lookup for kernels by BD + BDsub + optional KD. Admin-only. */
export async function exactMatchKernels(
  data: KernelMatchRequest,
): Promise<ApiResponse<{ botDimension: number; botDimensionSub: number; kernelDimension: number | null; kernels: KernelData[] }>> {
  return apiCall("/api/v1/kernel/exact_match", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export interface KernelBatchEmbedRequest {
  botDimension?: number;
}

export interface KernelBatchEmbedResponse {
  processed: number;
  failed: number;
  skipped: number;
}

/** Batch generate embeddings for all unembedded kernels. Admin-only. */
export async function batchEmbedKernels(
  data: KernelBatchEmbedRequest,
): Promise<ApiResponse<KernelBatchEmbedResponse>> {
  return apiCall("/api/v1/kernel/batch-embed", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export interface KernelAutoTagRequest {
  kernelKey: string;
}

export interface KernelAutoTagResponse {
  kernelKey: string;
  tags: string[];
}

/** Auto-generate tags for a kernel via LLM. Admin-only. */
export async function autoTagKernel(
  data: KernelAutoTagRequest,
): Promise<ApiResponse<KernelAutoTagResponse>> {
  return apiCall("/api/v1/kernel/auto-tag", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

// ── Bot Endpoints ──────────────────────────────────────────────────

export interface FetchBotRegistryParams {
  page?: number;
  /** Items per page. Use 500 to load all for selector dropdowns. */
  pageSize?: number;
  sortBy?: "botNumber" | "botName" | "createdAt";
  sortOrder?: "asc" | "desc";
  /** Case-insensitive contains filter on botName and botKey. */
  search?: string;
  /** Filter by mode: fast or quality. */
  mode?: string;
}

/** List all active bots — returns latest version per botKey with pagination support. */
export async function fetchBotRegistry(
  params?: FetchBotRegistryParams,
): Promise<ApiResponse<{ bots: BotData[]; total: number; page: number; pageSize: number; totalPages: number }>> {
  const p = new URLSearchParams();
  if (params?.page !== undefined) p.set("page", String(params.page));
  if (params?.pageSize !== undefined) p.set("pageSize", String(params.pageSize));
  if (params?.sortBy) p.set("sortBy", params.sortBy);
  if (params?.sortOrder) p.set("sortOrder", params.sortOrder);
  if (params?.search) p.set("search", params.search);
  if (params?.mode) p.set("mode", params.mode);
  const qs = p.toString();
  return apiCall(`/api/v1/bot/registry${qs ? `?${qs}` : ""}`);
}

export interface SearchBotsParams {
  query: string;
  page?: number;
  pageSize?: number;
  sortBy?: "botNumber" | "botName" | "createdAt";
  sortOrder?: "asc" | "desc";
  /** Filter by mode: fast or quality. */
  mode?: string;
}

/** Search bots across name, key, and classification. */
export async function searchBots(
  params: SearchBotsParams,
): Promise<ApiResponse<{ bots: BotData[]; total: number; page: number; pageSize: number; totalPages: number }>> {
  return apiCall("/api/v1/bot/search", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      query: params.query,
      page: params.page ?? 1,
      pageSize: params.pageSize ?? 20,
      sortBy: params.sortBy ?? "botNumber",
      sortOrder: params.sortOrder ?? "asc",
      ...(params.mode ? { mode: params.mode } : {}),
    }),
  });
}

export async function createBot(
  data: BotCreateRequest,
): Promise<ApiResponse> {
  return apiCall("/api/v1/bot/create_kernel_keys_ref_2_bot", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

// ── PROJECT-072: Bot Creation Automation (intent → bot) ────────────

/** Where a BD155 skill this bot needs actually lives right now. Answered at classify
 *  time by the same three stages construct runs, read-only (search, never install). */
export type SkillAvailability =
  | "in_raisy"     // already a kernel here, or on disk ready to materialise
  | "in_openclaw"  // exists in the ClawHub registry, will be fetched on construct
  | "must_build"   // nowhere — construct opens a dev request and builds the bot without it
  | "unknown";     // probe failed; construct will search again

/**
 * Another skill that also matched this slot's intent but was not the one resolved.
 *
 * The acquisition search scores every BD155 skill and then keeps one. These are the rest —
 * evidence that was computed and, until now, thrown away. Surfacing them is the whole point
 * of the picker: "email search bot" resolved the email-SEND skill because BD155.3 was the
 * only value label containing the word "email", while hunter-search sat one rank away under
 * BD155.8, scored, rejected, and never mentioned.
 */
export interface SkillAlternative {
  kernelKey: string;
  /** BD155.<sub> this kernel is filed under — NOT necessarily the slot's own sub. */
  sub: number | null;
  /** Cosine similarity against the intent, 0–1. Higher is closer. */
  score: number;
  kernelName: string;
  description: string;
  /**
   * True when this kernel lives in a different BD155 bucket than the classifier chose.
   * Picking it is still allowed — but it re-files the bot, so `classification` comes back
   * from construct rewritten (155.3.1 → 155.8). The UI must say so before the click, not
   * after: a silent reclassification is how a bot ends up claiming one category and
   * carrying another, which breaks reuse (bots dedup on the classification string).
   */
  crossCategory: boolean;
  /** Why it wasn't bound, in the search's own words. */
  reason: string;
}

/** Read-only triage of one BD155 slot, attached to its signature slot. */
export interface SkillSlotProbe {
  availability: SkillAvailability;
  /** "db" | "local" | "remote" — which stage answered. */
  source: string | null;
  /** Set when a specific kernel already satisfies the slot. */
  kernelKey: string | null;
  /** ClawHub slugs found for an `in_openclaw` slot. */
  candidates: string[];
  /** True when those candidates are unvetted: construct will HOLD the slot for review
   *  rather than install, so the bot gets built with the skill still pending. */
  requiresApproval: boolean;
  /** An existing dev request for this slot — "already requested" beats "must build". */
  devRequestId: string | null;
  devRequestStatus: DevRequestStatus | null;
  /** False when the remote registry was skipped or unreachable — `must_build` then means
   *  "not in raisy", not "nowhere on earth". */
  checkedRemote: boolean;
  /** Other skills that matched, ranked best-first. ABSENT (not empty) when the search found
   *  nothing else — so `alternatives?.length` distinguishes "one clear answer" from "we
   *  never looked", and the picker only appears where there is an actual choice. */
  alternatives?: SkillAlternative[];
}

/** The dimension index's own minting rule for a slot's dimension. Null when the index
 *  annotates nothing and `isCustom` fell back to LevelConfig's floor. */
export type SlotMintPolicy =
  | "always_custom"
  | "conditional_custom"
  | "shared"
  | "meta_excluded";

/** One BD slot in a derived bot signature, with reuse-vs-mint status. */
export interface BotSignatureSlot {
  /** e.g. "BD10". */
  dimension: string;
  /** BDsub value. */
  sub: number;
  /** KD extension — the optional third segment of the code (155.9.<kdSub>), refining
   * the BDsub. Not a slot of its own; null when the classifier emitted a 2-segment code. */
  kdSub?: number | null;
  /** Resolved kernel key for this slot, e.g. "raisy_BD10_1". */
  kernelKey: string;
  /** Coarse badge kept for every slot: "reuse" = kernel already exists; "mint" = it will
   * be authored on construct. For a BD155 slot this is a simplification — a skill is
   * acquired or requested, never minted — so prefer `skill.availability` when present. */
  status: "reuse" | "mint";
  /** True when this slot is authored per-bot rather than shared. */
  isCustom: boolean;
  /** Why: the index's tri-state, vs the collapsed `isCustom` boolean. */
  mintPolicy?: SlotMintPolicy | null;
  /** Present only on BD155 slots. */
  skill?: SkillSlotProbe | null;
  spec: string;
}

/**
 * A signature dimension the classifier ruled OUT, rendered dimmed next to the picked ones.
 *
 * Sub 0 is the index's own vocabulary: the classifier's rule is "DEFAULT TO ZERO … 0
 * meaning 'not applicable to this bot'". So BD30.0 states that Niche was considered and
 * rejected — it is not a placeholder. These never become kernels (`_fill_bot` drops sub-0
 * pairs), which is why they arrive separately from `slots` rather than inside it.
 */
export interface UnselectedSlot {
  /** e.g. "BD30". */
  dimension: string;
  /** Always 0 — "not applicable to this bot". */
  sub: 0;
  /** e.g. "BD30.0". */
  spec: string;
  /** Dimension name from the index, e.g. "Niche". */
  name: string;
  mintPolicy?: SlotMintPolicy | null;
  /** How many values this dimension offered before being ruled out — the difference
   * between "nothing was available" and "42 options, none applied". */
  valueCount: number;
}

/** A needed skill, flattened from its BD155 slot for the summary row. */
export interface RequiredSkill extends SkillSlotProbe {
  sub: number;
  kdSub?: number | null;
  spec: string;
  /** BD155 value label, e.g. "Social Media — post, scrape, analyse social platforms". */
  category: string;
}

/** Result of deriving a bot signature (the decide step — does NOT persist). */
export interface BotSignature {
  target: string;
  /** Full BD-code classification string, e.g. "BD999.1_BD10.1_BD2.3_BD1.1_BD0.1". */
  classification: string;
  /** Classifier bot that produced it. */
  classified_by: string;
  /** LLM-suggested bot name from the intent — pre-fills the (editable) Name field. */
  suggestedName?: string | null;
  /** Ordered slots BD999 → shared → BD1 → BD0, each with a reuse/mint badge. */
  slots: BotSignatureSlot[];
  /** The remaining signature dimensions, which the classifier ruled out. Shown dimmed so
   * a reader can tell a deliberate selection from a suspiciously short one. */
  unselectedSlots?: UnselectedSlot[];
  /** Whether this bot needs an external skill at all — a decision, not missing data.
   * False means the classifier judged it can do its job by reasoning alone (draft an
   * email); true means it must call something (send an email). */
  skillRequired?: boolean;
  /** One entry per needed skill, with where it currently lives. Empty when none. */
  skills?: RequiredSkill[];
}

export interface DeriveBotSignatureRequest {
  /** Plain-language intent, e.g. "send email on this topic" or "search for X". */
  target: string;
  bd999_schema?: Record<string, unknown>;
  entity_context?: Record<string, unknown>;
  /**
   * Entity barcode this bot is being built FOR. Distinct from `entity_context`, which is
   * descriptive text for the classifier — this is identity, and it changes what gets
   * built: the preview reports whether an entity-PRIVATE kernel exists for each slot.
   * Omit (or null) for a global bot.
   */
  entity_id?: string | null;
  /** Search the remote ClawHub registry for skills raisy doesn't have, so the preview can
   * distinguish "openclaw has it" from "must be built". Read-only, but slower — set false
   * for a local-only preview. Defaults to true server-side. */
  probe_remote_skills?: boolean;
}

/**
 * Derive a bot's signature for preview (BD slots + reuse/mint + classification).
 * Does NOT persist — commit with constructBot({ target }). Mirrors deriveKernelSignature.
 */
export async function deriveBotSignature(
  data: DeriveBotSignatureRequest,
): Promise<ApiResponse<BotSignature>> {
  // BE field is `bot_intent` (was `target`); map at the boundary.
  const { target, ...rest } = data;
  return apiCall("/api/v1/bot/intent_2_signature", {
    method: "POST",
    body: JSON.stringify({ bot_intent: target, ...rest }),
  });
}

export interface ConstructBotRequest {
  target: string;
  botName?: string;
  mode?: BotMode;
  /** Pre-derived classification (from deriveBotSignature) — builds from the reviewed
   * signature instead of re-classifying. Omit to let construct classify fresh. */
  classification?: string;
  entity_context?: Record<string, unknown>;
  /**
   * Entity barcode this bot is built FOR. Scopes the build on two axes:
   *  - kernel resolution — an entity-PRIVATE kernel is attached where one exists,
   *    and the bot is flagged `is_private`;
   *  - reuse — `bots.entity_id` (migration 098) joins classification + owner in the
   *    dedup key, so two entities with the same intent get two separate bots rather
   *    than one entity receiving another's assembly (which could not resolve for it
   *    and died at invoke time).
   * Omit (or null) for a global bot.
   */
  entity_id?: string | null;
  /** Registered DB-write destination (cfg/output_routing_registry.json). */
  outputDestination?: string;
  outputDestinationConfig?: Record<string, unknown>;
  /**
   * Explicit skill picks for BD155 slots, from the alternatives shown at derive time.
   *
   * A pick is not a hint — construct binds the named kernel and skips the acquisition
   * search entirely for that slot. That search is what got it wrong in the first place;
   * re-running it and hoping it agrees would defeat the point. If the key names no active
   * BD155 kernel the slot is left PENDING rather than quietly falling back to the search,
   * so a bad pick fails loudly instead of silently reinstating the wrong skill.
   */
  skillSelections?: SkillSelection[];
}

/** One BD155 slot's resolved-by-hand answer: which kernel goes in slot BD155.<sub>. */
export interface SkillSelection {
  /** The SLOT's sub as derived — not the chosen kernel's. They differ on a cross-category
   *  pick, which is exactly how the BE knows to rewrite the classification. */
  botDimensionSub: number;
  kernelKey: string;
}

/** A skill resolved/acquired for a BD155 slot during construction. */
export interface AcquiredSkill {
  kernelKey: string;
  /** Where the skill came from: existing DB kernel, local clawhub, or remote ClawHub. */
  source: "db" | "local" | "remote";
}

export interface ConstructBotResult {
  botId: string;
  botKey: string;
  botName: string;
  classification: string;
  kernel_keys_ref: string[];
  kernels_created: string[];
  kernels_reused: string[];
  /** BD0 input JSON schema authored + de-duplicated during construction. */
  inputSchema?: Record<string, unknown> | null;
  /** BD999 output JSON schema authored (derived backward from the target). */
  outputSchema?: Record<string, unknown> | null;
  /** Skills resolved/acquired for BD155 slots, each with its provenance. */
  skills_acquired?: AcquiredSkill[];
  outputDestination: string | null;
  /** "created" = new bot built; "reused" = existing bot bound (dedup). */
  action: "created" | "reused";
}

/**
 * Construct a complete bot from one intent: classify → resolve/create kernels →
 * assemble. Returns the created (or reused) bot. The commit step after deriveBotSignature.
 */
export async function constructBot(
  data: ConstructBotRequest,
): Promise<ApiResponse<ConstructBotResult>> {
  // BE field is `bot_intent` (was `target`); map at the boundary.
  const { target, ...rest } = data;
  return apiCall("/api/v1/bot/create_intent_2_bot_auto", {
    method: "POST",
    body: JSON.stringify({ bot_intent: target, ...rest }),
  });
}

/** Edit bot by creating a new version (immutable). */
export async function editBot(
  botKey: string,
  data: Omit<BotEditRequest, "botKey">,
): Promise<ApiResponse> {
  return apiCall(`/api/v1/bot/${encodeURIComponent(botKey)}/edit`, {
    method: "PATCH",
    body: JSON.stringify({ botKey, ...data }),
  });
}

/** Archive bot (soft-delete via new archived version). */
export async function archiveBot(botKey: string): Promise<ApiResponse> {
  return apiCall(`/api/v1/bot/${encodeURIComponent(botKey)}/archive`, {
    method: "POST",
  });
}

/** Get all versions of a bot (audit trail). */
export async function getBotVersions(
  botKey: string,
): Promise<ApiResponse<{ versions: BotVersionSummary[] }>> {
  return apiCall(`/api/v1/bot/${encodeURIComponent(botKey)}/versions`);
}

// ── Bot Execution Endpoints ───────────────────────────────────────

export interface BotRunRequest {
  botId: string;
  input_prompt: string;
  variables?: Record<string, string>;
  model?: string;
  thinking_level?: string;
  mode?: string;
  /** Entity this run happens for/within — e.g. lets a bot with the email-send skill
   * send as that entity's connected Gmail account. Omit for the platform default. */
  entity_id?: string | null;
}

export interface BotRunResponse {
  output: unknown;
  model: string;
  thinking_level: string;
  mode: string;
  latencyMs: number;
  tokenUsage?: { input: number; output: number };
  kernelIds: string[];
  botAssemblyHash: string;
  /** The bot_call_logs row this run was persisted as — lets a live run (not just
   * reviewed history) be Accepted/Rejected via evaluateCall(). Null if logging failed. */
  callId?: string | null;
  /** T0120: present (== "skill_not_attached") when invocation was blocked because the
   *  bot still has unresolved BD155 skill slot(s) — see pendingSkills for which ones. */
  reason?: string;
  pendingSkills?: PendingSkill[];
}

/** Run a bot — full LLM invocation. */
export async function runBot(
  data: BotRunRequest,
): Promise<ApiResponse<BotRunResponse>> {
  return apiCall("/api/v1/bot/invoke", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

// ── Bot Call History ──────────────────────────────────────────────

/** One review verdict. The current one lives at the root of `evaluation`; superseded
 *  ones move into `evaluation.history` (newest last). */
export interface CallEvaluationEntry {
  verdict: "approved" | "rejected";
  feedback?: string | null;
  evaluatedBy?: string;
  evaluatedAt?: string;
}

export interface CallEvaluation extends CallEvaluationEntry {
  /** T0223: previous verdicts on this same call, oldest first. Absent on calls that
   *  have only ever been reviewed once (and on every row written before T0223). */
  history?: CallEvaluationEntry[];
}

export interface BotCallHistoryEntry {
  callId: string;
  botId: string;
  kernelIds: string[];
  kernel_keys_ref?: string[];
  kernelVersions?: number[];
  botAssemblyHash: string | null;
  input_prompt: string;
  variables?: Record<string, string> | null;
  output: unknown;
  /** Failure reason for a failed call (output is null in that case). */
  error?: string | null;
  /** T0224: "success" | "failed". Before T0224 `DirectCallLogger` dropped this, so every
   *  raisy_61 / raisy_73 / raisy_997 row read as a success regardless of outcome. Rows
   *  written before then are still mislabelled. */
  status?: string | null;
  /** T0224: correlation id shared by the 61 call, the 73 call and the kernel version of
   *  one diagnose→patch sequence. Null on ordinary bot runs. */
  patchRunId?: string | null;
  /** T0224: the kernel a raisy_73 call actually edited. */
  targetKernelKey?: string | null;
  /** T0224: the kernels a raisy_61 call named, verbatim as the model wrote them (either
   *  directive slot) — including ones that resolved to nothing. */
  namedKernelKeys?: string[];
  evaluation: CallEvaluation | null;
  model: string | null;
  mode: string | null;
  latencyMs: number | null;
  inputTokens: number | null;
  outputTokens: number | null;
  timestamp: string | null;
  /** D-022: the call this row was composed from. Null on an original. */
  parentCallId?: string | null;
  /** D-022: the origin of this row's version lineage — equals `callId` on an original.
   *  Every version in one lineage shares it, so grouping needs no walk. */
  rootCallId?: string | null;
  /** D-022: 1 = the original model call, 2+ = a composed output version. */
  version?: number | null;
  /** T0826: how many versions this run has. Present ONLY on a `rootsOnly` listing —
   *  null elsewhere, so "not counted here" cannot be misread as "one version". */
  versionCount?: number | null;
  /**
   * P135 T1362 row 7 (half A) — the version bar's seed, when the caller asked for it
   * (`includeLineage`). Present ONLY then; absent means "not measured here", never "this call
   * has no versions" — the distinction `versionCount` keeps the same way.
   *
   * ⚠️ It carries no per-version `output`. Seeding the BAR from this is safe; landing the view
   * on `selectedCallId` before the text arrives is not. See `SeedVersion`.
   */
  lineage?: VersionLineageSeed | null;
  /** T0425: the call that WROTE the reply, when this row stands for a mailbox item the writer
   *  drafted. The item's own `callId` is the call that PRODUCED the item (a judge verdict); the
   *  change view follows THIS id so the markable text is the draft and rewrites land in the
   *  draft's version lineage. Null when the reply was written without a call row (a template) or
   *  not written at all. */
  draftCallId?: string | null;
}

export interface CallHistoryStats {
  total: number;
  approved: number;
  rejected: number;
  unevaluated: number;
  accuracyRate: number;
}

export interface CallHistoryResponse {
  calls: BotCallHistoryEntry[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    pages: number;
  };
  stats?: CallHistoryStats;
}

/**
 * Which slice of a bot's call history to show.
 *
 *   runs     — the bot's own executions.
 *   classify — the classifier call that produced its BD signature.
 *   build    — the rest of the construction chain (kernel/schema mints, naming).
 *
 * `classify` and `build` PARTITION the construction chain: every chain call carries
 * `input.variables.construct_bot_key`, and the classifier ones appear only under `classify`.
 * Omit for everything.
 */
export type CallRelation = "runs" | "build" | "classify";

export interface CallHistoryFilters {
  botId?: string;
  botKey?: string;
  relation?: CallRelation;
  verdict?: "approved" | "rejected" | "unevaluated";
  entityId?: string;
  /** Filter by BD classification number present in kernel_keys_ref (e.g. 1 for BD1). */
  bd?: number;
  /** T0224 kernel-activity axis: every call that concerns this kernel — raisy_73 calls
   *  that patched it, raisy_61 calls that named it, and runs that assembled it. */
  targetKernelKey?: string;
  /** T0224: one diagnose→patch sequence, by the id strategize (or a heal run) minted. */
  patchRunId?: string;
  startDate?: string;
  endDate?: string;
  /**
   * T0826 — collapse each output-version lineage to its original row, and return
   * `versionCount` on it.
   *
   * A composed v+1 (D-014) is a real row under the original bot's id, so a run list shows
   * one piece of work N times. A version is not a run: no model was invoked to make it.
   *
   * ⚠️ Leave this off wherever individual versions ARE the subject — the diff modal and
   * the patch-run views read this same endpoint and would come back empty.
   */
  rootsOnly?: boolean;
  /** P135 T1362 row 7 (half A) — ask for each row's version-bar seed. Opt-in, and the BE field
   *  defaults to false, so every caller that does not set this is byte-identical to before. */
  includeLineage?: boolean;
  page?: number;
  pageSize?: number;
  includeStats?: boolean;
}

/** Fetch bot call logs with filtering, pagination, and aggregate statistics. */
export async function getCallHistory(
  filters: CallHistoryFilters = {},
): Promise<ApiResponse<CallHistoryResponse>> {
  const body: Record<string, unknown> = {
    page: filters.page ?? 1,
    pageSize: filters.pageSize ?? 20,
    includeStats: filters.includeStats ?? true,
  };
  if (filters.rootsOnly) body.rootsOnly = true;
  if (filters.includeLineage) body.includeLineage = true;
  if (filters.botId) body.botId = filters.botId;
  if (filters.botKey) body.botKey = filters.botKey;
  if (filters.relation) body.relation = filters.relation;
  if (filters.verdict) body.verdict = filters.verdict;
  if (filters.entityId) body.entityId = filters.entityId;
  if (filters.bd != null) body.bd = filters.bd;
  if (filters.startDate) body.startDate = filters.startDate;
  if (filters.endDate) body.endDate = filters.endDate;

  return apiCall("/api/v1/bot/call-history", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/** Submit evaluation (approve/reject) for a bot call. Approving also promotes
 * the bot's lifecycle status to "active" server-side — botStatus reflects that. */
export async function evaluateCall(
  callId: string,
  verdict: "approved" | "rejected",
  feedback?: string,
): Promise<ApiResponse<{ callId: string; evaluation: CallEvaluation; botStatus: string | null }>> {
  return apiCall("/api/v1/bot/call_2_verdict", {
    method: "POST",
    body: JSON.stringify({ callId, verdict, feedback }),
  });
}

/** P144 D-144-019 — RECORD THAT A PREPARED DRAFT WAS OPENED (first view wins, server-side).
 *
 * OPENING IS THE EVENT: not listing the row, and not deciding on it. The drafts surface calls this
 * when a reader opens a draft; nothing else does. It is deliberately NOT folded into
 * `/bot/call-history`, because that read feeds the Decide queue and the portfolio's own count as
 * well — a stamp written by a listing would record "someone fetched this page" as "the owner read
 * it", and the 50% quiet state would then fire for a number nobody had looked at.
 *
 * Fire-and-forget at the call site: a stamp must never hold up the reader's click, and the count it
 * feeds is refetched by the shared attention query afterwards.
 */
export async function recordDraftView(
  callId: string,
): Promise<ApiResponse<{
  callId: string;
  viewedAt: string | null;
  viewedBy: string | null;
  alreadyViewed: boolean;
}>> {
  return apiCall("/api/v1/bot/draft_2_view", {
    method: "POST",
    body: JSON.stringify({ callId }),
  });
}

// ── Source Ingestion (PROJECT-070) ─────────────────────────────────

export interface IngestFromSourceRequest {
  source_text: string;
  source_name?: string;
  source_type?: string;
  auto_approve?: boolean;
  /** The BE has always accepted this; the type did not, so a caller could not pass it. */
  model_tier?: "fast" | "medium" | "quality";
}

export interface IngestArtifact {
  type: "process_pattern" | "kernel_pattern" | "bot_pattern" | "entity_pattern";
  id: string;
  key: string;
  title?: string;
  classification?: string;
  classification_key?: string;
  classification_sub?: string | number;
  classification_sub_key?: string;
  entity_scope?: "general" | "entity_specific" | "partly_entity_specific";
  entity?: string;
  entity_type?: string;
  entity_dimension?: string;
  entity_id?: string | null;
  content?: string;
  reused?: boolean;
}

/**
 * T0265 — a PLANNED artifact: what the mint WOULD create, said before it does (@R01 §02 —
 * "show a preview BEFORE expensive builds").
 *
 * There is no `id` here, deliberately: nothing has been minted. `segment` carries the planner's own
 * dict, and approval mints from THAT — which is what makes the preview true rather than indicative.
 * An LLM asked twice can answer differently, so a preview built on a second plan would describe a
 * chain the person never gets.
 */
export interface PlannedArtifact {
  planned: true;
  index: number;
  type: IngestArtifact["type"];
  classification: string;
  /** The value's own name, when the planner returned one (D-201-008). */
  label?: string | null;
  title?: string;
  excerpt?: string;
  confidence?: number;
  reason?: string;
  /** What the mint will do with this one. */
  action: "create" | "merge" | "reuse" | "unplaced";
  /** The same decision in words, written to be read by a person. */
  action_words: string;
  existing_key?: string;
  scope?: string;
  entity_id?: string | null;
}

/** The chain in one line, so the count is legible before it is real. */
export interface ChainSummary {
  total: number;
  create: number;
  merge: number;
  reuse: number;
  unplaced: number;
  words: string;
}

export interface IngestFromSourceResponse {
  ingestion_id: string;
  status: string;
  artifacts_count: number;
  artifacts: IngestArtifact[];
}

export interface IngestJobResponse {
  ingestion_id: string;
  source_type: string;
  source_name: string | null;
  status: string;
  progress_percent: number;
  artifacts: IngestArtifact[];
  /** T0265: the job is parked between the plan and the mint — NOTHING has been built yet. */
  awaiting_approval?: boolean;
  /** The chain structure: what this source WOULD become, before it becomes it. */
  chain?: PlannedArtifact[];
  summary?: ChainSummary;
  logs: { stage: string; message: string; created_at: string }[];
  uploaded_source: {
    id: string;
    original_filename: string;
    mime_type: string;
    size_bytes: number;
    download_url: string;
    s3_uri: string;
  } | null;
  created_at: string | null;
  updated_at: string | null;
}

/** Start ingesting a source document into reusable patterns. */
export async function ingestFromSource(
  data: IngestFromSourceRequest,
): Promise<ApiResponse<IngestFromSourceResponse>> {
  return apiCall("/api/v1/ingest/source_2_patterns", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

/**
 * Bring a LINK in — the words a post was spoken with, plus the media it carried.
 *
 * The link is whatever the user pasted: a full post address OR LinkedIn's own share short link
 * (`lnkd.in/...`), which carries no post id of its own. The BE follows the redirect, so the FE
 * never has to know the difference. `submitted_url` echoes back what he pasted; `post_url` is what
 * it resolved to — both belong on the receipt (W2: nothing hidden).
 *
 * A refusal (a host we cannot read yet, an empty link) comes back as a MESSAGE the surface can show,
 * never as a fault to swallow (W6 + W10 iv).
 *
 * `tried` is what the LADDER attempted, in its own words, when neither engine could read the link —
 * "media: … · page: …". It is shown rather than a vague "coming soon" because a limit that does not
 * name its reason is indistinguishable from a bug (T0244).
 *
 * TWO SIZES, because they can differ (T0246): `media_bytes` is what we KEPT, `speech_bytes` is what we
 * READ. A long video is past the size we can hold, so its speech is fetched as a small audio rendition
 * of itself — kept 0, read 2 MB.
 */
export interface IngestLinkResponse {
  resolved: {
    kind: string;
    author?: string | null;
    headline?: string | null;
    post_url?: string | null;
    submitted_url?: string | null;
    transcript_source?: string | null;
    media_bytes?: number | null;
    media_mime?: string | null;
    speech_bytes?: number | null;
    thumbnail_url?: string | null;
    state?: string | null;
    tried?: string | null;
  };
  ingestion_id?: string;
  // D-201-018: the endpoint answers inside a deadline, so for a slow media host the id arrives
  // BEFORE the words do — `reading: true` is a receipt whose sizes are still coming. The surface
  // must be able to say that rather than show a receipt with nothing in it.
  reading?: boolean;
  // D-201-021 (T0262): the OTHER half of the same budget. The words are already in hand and the
  // job already holds them; what is still running is the UPLOAD of the video we downloaded. So
  // `keeping_media: true` is the opposite receipt to `reading` — the words are here, the bytes
  // are not — and it carries NO sizes, because `media_bytes` renders as "Video kept" and those
  // bytes are not kept yet (W6).
  keeping_media?: boolean;
}

export async function ingestLink(
  link: string,
  opts: {
    sourceName?: string;
    autoApprove?: boolean;
    scope?: "global" | "private";
    modelTier?: "fast" | "medium" | "quality";
  } = {},
): Promise<ApiResponse<IngestLinkResponse>> {
  return apiCall("/api/v1/ingest/link_2_patterns", {
    method: "POST",
    // Exactly the five fields the BE batch proved (tests/linkedin_post_intake/test_input_02b_*.json).
    // No field is invented here — the reference file IS the contract.
    body: JSON.stringify({
      link,
      source_name: opts.sourceName?.trim() || null,
      auto_approve: opts.autoApprove ?? false,
      model_tier: opts.modelTier ?? "fast",
      scope: opts.scope ?? "global",
    }),
  });
}

/**
 * T0266 — ANOTHER GO on a source already brought (capability rows #10 and #11).
 *
 * The JOB decides the door, so the caller does not have to know which fix applies:
 *   · it holds the words -> the same source runs again FROM those words. Nothing is re-downloaded
 *     or re-uploaded — change the tier first and this is "the same source at a higher tier";
 *   · the read never landed -> the link the job came from is read again, through the same route.
 *
 * A NEW job comes back (the first one keeps its own record of what happened), so callers must read
 * `data.ingestion_id` and follow that. A job with neither words nor a link is refused with a reason.
 */
export async function anotherGoOnIngestJob(
  jobId: string,
  opts: {
    modelTier?: "fast" | "medium" | "quality";
    autoApprove?: boolean;
    preview?: boolean;
    sourceName?: string;
  } = {},
): Promise<
  ApiResponse<{
    ingestion_id: string;
    source_job_id: string;
    run_from: "words_already_held" | null;
    characters: number;
    model_tier: string;
    status: string;
  }>
> {
  const q = new URLSearchParams();
  if (opts.modelTier) q.set("model_tier", opts.modelTier);
  if (opts.autoApprove !== undefined) q.set("auto_approve", String(opts.autoApprove));
  if (opts.preview !== undefined) q.set("preview", String(opts.preview));
  if (opts.sourceName) q.set("source_name", opts.sourceName);
  const query = q.toString();
  return apiCall(
    `/api/v1/ingest/job/${encodeURIComponent(jobId)}/another-go${query ? `?${query}` : ""}`,
    { method: "POST" },
  );
}

/** Poll ingestion job status and artifacts. */
export async function getIngestJob(
  jobId: string,
): Promise<ApiResponse<IngestJobResponse>> {
  return apiCall(`/api/v1/ingest/job/${encodeURIComponent(jobId)}`);
}

/**
 * T0265 — THE GO: build the chain that was previewed (@R01 §02).
 *
 * The BE mints from the plan it already stored, never from a second planner call, so what was
 * approved is what is built. A 409 comes back when the job is not waiting for a go — which is also
 * what stops a second click from building the same chain twice, so callers must READ `status`.
 */
export async function approveIngestJob(
  jobId: string,
  autoApprove = false,
): Promise<ApiResponse<{ ingestion_id: string; artifacts_count: number; artifacts: IngestArtifact[] }>> {
  return apiCall(
    `/api/v1/ingest/job/${encodeURIComponent(jobId)}/approve?auto_approve=${autoApprove ? "true" : "false"}`,
    { method: "POST" },
  );
}

/**
 * T0265 — THE NO: end the job having minted nothing. A gate with no "no" is not a gate.
 *
 * Nothing is undone because nothing was built — the library is left exactly as it was.
 */
export async function discardIngestJob(
  jobId: string,
  reason?: string,
): Promise<ApiResponse<{ ingestion_id: string; summary: ChainSummary }>> {
  const query = reason ? `?reason=${encodeURIComponent(reason)}` : "";
  return apiCall(`/api/v1/ingest/job/${encodeURIComponent(jobId)}/discard${query}`, {
    method: "POST",
  });
}

/** Upload a file and start the ingest pipeline. */
export async function uploadAnythingFile(
  formData: FormData,
): Promise<ApiResponse<{ ingestion_id: string; source_id: string; status: string; download_url?: string; s3_uri?: string }>> {
  return apiCall("/api/v1/ingest/upload-file", {
    method: "POST",
    body: formData,
  });
}

/** Stream ingestion job updates via Server-Sent Events. */
export async function streamIngestJob(
  jobId: string,
  onEvent: (event: { type: "job"; data: IngestJobResponse }) => void,
): Promise<EventSource> {
  const token = await AuthUtils.ensureAuthToken();
  const es = new EventSource(
    `${API_URL}/api/v1/ingest/job/${encodeURIComponent(jobId)}/stream?token=${encodeURIComponent(token)}`,
    { withCredentials: true },
  );
  es.onmessage = (msg) => {
    try {
      const data = JSON.parse(msg.data) as IngestJobResponse;
      onEvent({ type: "job", data });
    } catch (e) {
      // Ignore malformed SSE frames
    }
  };
  es.onerror = (err) => {
    console.error("SSE error", err);
  };
  return es;
}

// ── Kernel Patching (raisy_73) ───────────────────────────────────

export interface PatchPreviewResponse {
  kernelKey: string;
  currentVersion: number;
  sp_current: string;
  sp_new_content: string;
  delta_ops: Array<{
    index: number;
    op: string;
    anchor_start?: string;
    anchor_end?: string;
    anchor_text?: string;
    new_content?: string;
    location?: string;
    field?: string;
    new_value?: string;
    rationale?: string;
  }>;
  applied_ops_preview: Array<Record<string, unknown>>;
  skipped_ops_preview: Array<Record<string, unknown>>;
  /** PROJECT-097 T0405 (IN-04): is this kernel assembled into more than one bot? */
  sharing?: KernelSharing;
  /** Advisory lint — content that would make a SHARED kernel serve one bot. Empty for
   *  custom kernels. Does not block; /apply-patch's 409 is what enforces. */
  specialisation_warnings?: string[];
  /** The tier the patch actually ran on, and whether the server overrode the request. */
  tier?: TierDecision;
  model?: string;
  thinking_level?: string;
}

/** PROJECT-097 T0217: derived live from bots.kernel_keys_ref, never from a stored column. */
export interface KernelSharing {
  sharing: "shared" | "custom";
  /** -1 means the server could not determine it and failed safe to "shared". */
  bot_count: number;
  /** Capped at 10 server-side. */
  bot_ids: string[];
}

/** PROJECT-097 B22: diagnosis and patching are upgraded off the fast tier server-side. */
export interface TierDecision {
  model: string;
  thinking_level: string;
  upgraded: boolean;
  reason?: string | null;
}

export interface BotContextForPatch {
  bot_name?: string;
  other_kernels?: { key: string; name: string; tokenCount: number }[];
  last_input?: string;
  last_output?: string;
  detected_variables?: string[];
  /** All {input} and [INTERNAL] parameters found across bot kernels, with counts per kernel */
  parameters?: {
    name: string;
    type: "input" | "internal";
    totalCount: number;
    kernels: string[];
  }[];
  /** T0220 (IN-06): the verbatim failure. Without it raisy_73 only ever saw the human's
   *  paraphrase, so a schema violation naming the exact missing property never reached
   *  the component that had to fix it. */
  last_error?: string;
}

/** Generate a raisy_73 patch preview for a kernel (does NOT save). */
export async function patchKernel(
  kernelKey: string,
  instructions: string,
  model?: string,
  thinking_level?: string,
  bot_context?: BotContextForPatch,
  /** T0224: the id strategizeBotKernel returned, when this patch follows a diagnosis. */
  patchRunId?: string,
  /** T0224: audit only — which bot this patch is being made for. */
  botId?: string,
): Promise<ApiResponse<PatchPreviewResponse>> {
  return apiCall("/api/v1/kernel/patch", {
    method: "POST",
    body: JSON.stringify({
      kernelKey, instructions, model, thinking_level, bot_context, patchRunId, botId,
    }),
  });
}

export interface AddReferencesToBd0Result {
  kernelKey: string;
  /** Names actually added — excludes any BD0 already declared. */
  added: string[];
  /**
   * TJ05b — the version this edit is byte-identical to, or null.
   *
   * NOT a duplicate that should have been suppressed: toggling `required` on then off leaves
   * the schema exactly as it was two versions ago, and the second toggle is a real change the
   * bot is now in. Suppressing it would mean the operator turns `required` off and the
   * contract keeps demanding the field. Reported like a `git revert` — a new entry whose
   * content matches an older one.
   */
  revertsToVersion: number | null;
  /**
   * Bots whose cached `inputSchema` was refreshed because the name was ALREADY in the BD0
   * kernel. Non-zero means the panel was listing a property the contract already had — the
   * column had drifted, not the kernel — and that drift has now been corrected.
   */
  refreshedBots?: number;
}

/**
 * PROJECT-099B TC04 — declare undeclared references on the bot's BD0 kernel.
 *
 * The human decision the automatic path is not allowed to make: T0230 refuses to add a
 * property found only as a bare bracket in kernel text, because a scan cannot tell a real
 * input from a specimen. Clicking this supplies that missing declaration.
 *
 * Writes the BD0 KERNEL, not `bots.inputSchema` — saving the kernel invalidates the bot's
 * cached schema, so file and contract stay the same document.
 */
export async function addReferencesToBd0(
  botId: string,
  names: string[],
  entity_id?: string,
  /**
   * TJ15 — the description to store per name, as the operator approved it. A name omitted
   * here gets one derived server-side from the bot's OWN kernels, and only when unambiguous.
   *
   * ⚠️ NOT COSMETIC. `_render_field` emits this as `note="..."` on every field of every
   * call, so it is an instruction to the model. A property declared with an empty one
   * reaches the model as a bare name whose meaning it has to guess.
   */
  descriptions?: Record<string, string>,
  /**
   * TJ16 — JSON Schema type per name. Omit to let the server derive it from the bot's own
   * kernels, falling back to "string".
   *
   * ⚠️ This used to be hardcoded `"string"`. Type is ENFORCED at every call, so declaring a
   * parameter that callers pass as a list as a string did not merely mislabel it — every
   * subsequent call to that bot failed validation.
   */
  types?: Record<string, string>,
): Promise<ApiResponse<AddReferencesToBd0Result>> {
  return apiCall("/api/v1/kernel/add_references_2_bd0", {
    method: "POST",
    body: JSON.stringify({ botId, names, entity_id, descriptions, types }),
  });
}

/** A (kernelKey, name) pair a human judged not to be an input. PROJECT-099B TC06. */
/**
 * TJ02 — the undo for `addReferencesToBd0`. Removes properties from the bot's BD0 kernel.
 *
 * The backend prunes the `required` array alongside `properties`: a name left in `required`
 * with no matching property is a schema demanding a field that cannot be supplied, which
 * fails validation on every subsequent run.
 *
 * ⚠️ Removing a property RE-ARMS the undeclared-reference warning for that token. That is
 * correct — the kernel still references it and BD0 no longer declares it.
 */
export async function removeReferencesFromBd0(
  botId: string,
  names: string[],
  entityId?: string,
): Promise<ApiResponse<{
  kernelKey: string;
  removed: string[];
  revertsToVersion: number | null;
}>> {
  return apiCall("/api/v1/kernel/remove_references_2_bd0", {
    method: "POST",
    body: JSON.stringify({ botId, names, entity_id: entityId }),
  });
}

/** TJ14 — one occurrence of a parameter token in a kernel's text. */
export interface ParameterUsage {
  /** 1-based line within the kernel content. */
  line: number;
  /** 0-based offset of the match WITHIN `text` — not within the original line. */
  column: number;
  /** The token exactly as written, e.g. `[tone]` or `{TONE}`. */
  matched: string;
  /** A window around the match; `…` marks a truncated edge. */
  text: string;
}

/** TJ14 — one kernel that names the parameter. */
export interface ParameterUsageKernel {
  kernelKey: string;
  kernelName: string;
  botDimension: number | null;
  versionNumber: number | null;
  occurrences: number;
  /** The name is in this kernel's `inputVariables` COLUMN, not only its text. */
  declaredInColumn: boolean;
  usages: ParameterUsage[];
}

/**
 * TJ14 — where is this parameter used? Read-only; nothing is changed.
 *
 * THE ORDERING IS THE POINT. TJ09 renamed BD0 and then reported which kernels it had just
 * broken, which is not information after the commit — it is a bill. This answers the
 * question while the operator can still decide not to.
 */
/**
 * TJ16 — the JSON Schema types a BD0 property may declare.
 *
 * `null` is absent deliberately: a null-typed property can only be supplied as null, and the
 * validator strips nulls from the payload before checking — so it is unsatisfiable by
 * construction. Mirrors `BD0_PROPERTY_TYPES` in `kernel_loader.py`, which is the authority.
 */
export const BD0_PROPERTY_TYPES = [
  "string", "number", "integer", "boolean", "array", "object",
] as const;
export type Bd0PropertyType = (typeof BD0_PROPERTY_TYPES)[number];

/** TJ15/TJ16 — an authored description and/or type for a parameter, and where it came from. */
export interface DescriptionCandidate {
  description: string;
  /** The JSON Schema type that kernel declares, or "" if it declares none. */
  type: string;
  kernelKey: string;
  /** From a kernel in THIS bot's assembly — the only tier applied without review. */
  inAssembly: boolean;
  occurrences: number;
}

/**
 * TJ16 — edit an existing BD0 property's description and/or type.
 *
 * ⚠️ THE TWO ARE NOT EQUALLY SAFE. `description` is rendered to the model as `note="..."`;
 * changing it changes what the model is told and nothing rejects a payload. `type` is
 * ENFORCED by `jsonschema_validate` on every call, so switching `string` → `array` makes
 * every caller still sending a string fail outright — the bot stops running. Warn before,
 * not after.
 *
 * Pass `undefined` to leave a field alone; pass `""` for `description` to clear it.
 */
export async function setPropertyMeta(
  botId: string,
  name: string,
  changes: { description?: string; type?: Bd0PropertyType },
  entityId?: string,
): Promise<ApiResponse<{
  kernelKey: string;
  name: string;
  changed: string[];
  revertsToVersion: number | null;
}>> {
  return apiCall("/api/v1/kernel/set_property_meta_2_bd0", {
    method: "POST",
    body: JSON.stringify({ botId, name, ...changes, entity_id: entityId }),
  });
}

export async function findReferenceUsages(
  botId: string,
  name: string,
  entityId?: string,
): Promise<ApiResponse<{
  name: string;
  kernels: ParameterUsageKernel[];
  totalOccurrences: number;
  /** TJ15 — the description to pre-fill. Empty when nothing authored exists. */
  suggestedDescription: string;
  /**
   * True only when the bot's OWN assembly supplies exactly one distinct wording. A corpus
   * description is never confident: measured over the 725-kernel mirror, 70 of 148 described
   * names are described DIFFERENTLY by different kernels, and the disagreements are bots
   * meaning genuinely different things by the same word.
   */
  descriptionConfident: boolean;
  /** TJ16 — the type to pre-select. Falls back to "string" when nothing is known. */
  suggestedType: string;
  /** True only when the bot's OWN assembly agrees on one type. */
  typeConfident: boolean;
  descriptionCandidates: DescriptionCandidate[];
}>> {
  return apiCall("/api/v1/kernel/find_reference_usages", {
    method: "POST",
    body: JSON.stringify({ botId, name, entity_id: entityId }),
  });
}

/**
 * TJ09 — rename a BD0 property. The new name is normalised to UPPER_SNAKE server-side.
 *
 * ⚠️ A kernel reading `[RECIPIENT]` resolves against the BD0 field of that name; rename the
 * property to `TO` and the two stop matching, with no error anywhere — the model is handed a
 * value under a name its instructions never mention.
 *
 * `rewriteKernels` (TJ14) decides who fixes that:
 *   * `false` — BD0 only. `staleKernels` lists every kernel still carrying the old token and
 *     the caller MUST show it; finishing the rename means fixing those through TC05. Right
 *     when the kernels need prose changes a token swap would not cover.
 *   * `true` — every affected kernel is rewritten too, each minting a new version. Preview
 *     the exact impact with `findReferenceUsages` first.
 *
 * ⚠️ NOT ATOMIC. BD0 is renamed first, so a mid-way failure degrades to the `false` outcome —
 * reported in `staleKernels`, and finishable by hand. `failed` names any kernel that threw.
 */
export async function renamePropertyInBd0(
  botId: string,
  oldName: string,
  newName: string,
  entityId?: string,
  rewriteKernels = false,
): Promise<ApiResponse<{
  kernelKey: string;
  oldName: string;
  newName: string;
  changed: boolean;
  staleKernels: string[];
  revertsToVersion: number | null;
  rewritten: {
    kernelKey: string;
    kernelName: string;
    occurrences: number;
    columnEntries: number;
    versionNumber: number | null;
  }[];
  failed: { kernelKey: string; error: string }[];
}>> {
  return apiCall("/api/v1/kernel/rename_property_2_bd0", {
    method: "POST",
    body: JSON.stringify({ botId, oldName, newName, entity_id: entityId, rewriteKernels }),
  });
}

/**
 * TJ03 — mark a BD0 property required, or optional. Was write-once before this: a property
 * declared from a bare prose reference is always created optional, because nothing had
 * asserted it was mandatory.
 */
export async function setReferenceRequired(
  botId: string,
  name: string,
  required: boolean,
  entityId?: string,
): Promise<ApiResponse<{ kernelKey: string; name: string; changed: boolean }>> {
  return apiCall("/api/v1/kernel/set_required_2_bd0", {
    method: "POST",
    body: JSON.stringify({ botId, name, required, entity_id: entityId }),
  });
}

export interface ReferenceDismissal {
  kernelKey: string;
  name: string;
  /** Why the operator judged this not to be an input. Null on rows recorded before the
   *  Admin Bot Testbench started asking, and on any written directly against the database. */
  reason?: string | null;
  /** The kernel version the operator was actually looking at when they decided. */
  dismissedAtVersion?: number | null;
  dismissedBy?: string | null;
}

/**
 * TC06 — record (or undo) "this reference is not an input", for ONE BOT and one kernel
 * LINEAGE. `[delta_ops]` being documentation in one kernel says nothing about whether it is
 * a real input in another.
 *
 * `botKey` is required (migration 000007). Kernel-only scoping let a single judgement about
 * a SHARED kernel — `raisy_BD998_1` is on every bot — silence that warning everywhere,
 * including on a bot that genuinely needs the input and has not declared it in BD0. Both
 * arguments are LINEAGE keys, never `botId`/`kernelId`, which die at the next version.
 */
export async function dismissReference(
  botKey: string,
  kernelKey: string,
  name: string,
  reason?: string,
  undo = false,
): Promise<ApiResponse<{ botKey: string; kernelKey: string; name: string; dismissed: boolean }>> {
  return apiCall("/api/v1/kernel/dismiss_reference", {
    method: "POST",
    body: JSON.stringify({ botKey, kernelKey, name, reason, undo }),
  });
}

/**
 * TJ13 — register every reference variable a bot has, in one call.
 *
 * Replaces the per-token "not an input" button. Not-an-input is the DEFAULT for everything
 * in that list (T0230 measured ~22 of 31 distinct corpus tokens as not inputs), so asking a
 * person to confirm it one row at a time produced a backlog, not a judgement. The table is
 * a CATALOGUE now; `reason` remains as optional annotation for the cases worth explaining.
 *
 * Idempotent — safe to call on every load, which is how it is used. A bot gains kernels
 * over its life, so registering once at creation would go stale.
 */
export async function registerReferenceVariables(
  botKey: string,
  references: { kernelKey: string; name: string }[],
): Promise<ApiResponse<{ added: number; existing: number; dismissals: ReferenceDismissal[] }>> {
  return apiCall("/api/v1/kernel/register_reference_variables", {
    method: "POST",
    body: JSON.stringify({ botKey, references }),
  });
}

export async function listReferenceDismissals(
  botKey: string,
  kernelKeys: string[],
): Promise<ApiResponse<{ dismissals: ReferenceDismissal[] }>> {
  const q = encodeURIComponent(kernelKeys.join(","));
  const b = encodeURIComponent(botKey);
  return apiCall(`/api/v1/kernel/reference_dismissals?kernelKeys=${q}&botKey=${b}`, {
    method: "GET",
  });
}

export interface ApplyPatchResult {
  kernelId: string;
  kernelKey: string;
  versionNumber: number;
  contentTokenCount: number;
  /** Receipt naming the blast radius of a confirmed shared edit. */
  sharing?: KernelSharing;
}

/**
 * PROJECT-097 T0407: the 409 body returned when a shared kernel is edited without opting
 * in. Deliberately not merged into ApplyPatchResult — a 409 is a question, not a failure,
 * and conflating them is how the block would end up rendered as "Apply failed".
 */
export interface SharedEditBlocked extends KernelSharing {
  requires: string;
  why: string;
  specialisation_warnings: string[];
}

/**
 * Apply a reviewed patch — saves patched content as new kernel version.
 *
 * Returns statusCode 409 with a SharedEditBlocked body when `kernelKey` is assembled into
 * more than one bot and `allowSharedEdit` is false. Re-call with allowSharedEdit=true to
 * confirm. The default is false on purpose: 22 rows are already `poisoned_shared` because
 * nothing ever asked.
 */
export async function applyKernelPatch(
  kernelKey: string,
  patchedContent: string,
  description?: string,
  allowSharedEdit = false,
  /** T0224: stamped into the new version's description as `[run=<id>]`, which is what
   *  lets the version be traced back to the 61/73 calls that produced it. */
  patchRunId?: string,
): Promise<ApiResponse<ApplyPatchResult | SharedEditBlocked>> {
  return apiCall("/api/v1/kernel/apply-patch", {
    method: "POST",
    body: JSON.stringify({ kernelKey, patchedContent, description, allowSharedEdit, patchRunId }),
  });
}

// ── Bot-level strategizing (raisy_61 — PROJECT074) ────────────────

export interface RefactoringDirective {
  target_sp_prompt_id: string;
  target_sp_prompt_name: string;
  target_component: string;
  problem_change_directive: string;
  imperative_change_directive: string;
  optimization_flag: boolean;
  problem_severity?: number;
  /** Resolved server-side from target_sp_prompt_id via the bot's kernel_index — the
   *  kernel this directive should be applied to. Null if raisy_61 named an id that
   *  doesn't match any kernel currently in the assembly. */
  kernelKey: string | null;
  resolved: boolean;
  /** Which slot the kernelKey was recovered from. B10: raisy_61 routinely puts a kernelKey
   *  in the id slot, or a kernelId in the name slot. */
  resolved_via?: string;
  /** T0403: the directive names literal text that does NOT appear in the kernel it blames —
   *  usually the sign it is describing runtime output rather than the prompt. */
  actionability_warnings?: ActionabilityWarning[];
}

export interface ActionabilityWarning {
  kind: string;
  detail: string;
}

/** Mirrors services/fault_classifier.FaultVerdict.to_dict(). */
export interface FaultVerdict {
  fault_class: "KERNEL_FAULT" | "CODE_FAULT" | "CONFIG_FAULT" | "DATA_FAULT" | "TRANSIENT_FAULT" | "CAPABILITY_FAULT" | string;
  confidence: number;
  evidence: string;
  code_change_required: boolean;
  dev_message?: string | null;
  user_message?: string | null;
}

/** T0204 (B07): raisy_61 returning zero directives is a VERDICT — "none of these kernels
 *  caused it" — not a malfunction. Non-null only when every kernel was cleared. */
export interface KernelsClearedVerdict extends FaultVerdict {
  escalated: boolean;
  next_step: string;
}

export interface StrategizeResponse {
  botId: string;
  /** Set when the diagnosis read one call's kernels rather than the bot's assembly. */
  callId?: string | null;
  /** "bot" = the bot's registered assembly, "call" = the kernels that call actually ran. */
  scope?: "bot" | "call";
  refactoring_directives: RefactoringDirective[];
  model?: string;
  thinking_level?: string;
  tier?: TierDecision;
  kernels_cleared?: KernelsClearedVerdict | null;
  /** T0224: correlation id minted for this diagnosis. Pass it to patchKernel and
   *  applyKernelPatch so the 61 call, the 73 call and the resulting kernel version can be
   *  read back as one sequence. Present even when every kernel was cleared. */
  patchRunId?: string;
}

// ── Fault triage (raisy_96 — PROJECT-097 T0200) ───────────────────

export type FaultClassName =
  | "KERNEL_FAULT"
  | "CODE_FAULT"
  | "CONFIG_FAULT"
  | "DATA_FAULT"
  | "TRANSIENT_FAULT"
  | "CAPABILITY_FAULT";

export interface FaultClassification extends FaultVerdict {
  /** "deterministic" | "raisy_96" | "deterministic (llm declined)" | … */
  source: string;
  llm_consulted: boolean;
  llm_raw?: Record<string, unknown> | null;
  note?: string | null;
  /**
   * The ONLY field to gate the Diagnose button on. Server-derived so "an affirmative
   * kernel fault unlocks diagnosis" has one definition rather than one per client.
   */
  diagnosis_appropriate: boolean;
}

/**
 * Classify a failed bot run before diagnosing it.
 *
 * Deterministic rules answer most failures for free; raisy_96 (fast tier) is consulted only
 * for what they cannot decide. Classification only — this never files a dev_request, because
 * a human is watching. Safe to call on every failure.
 */
export async function classifyFault(params: {
  botId: string;
  message: string;
  statusCode?: number | null;
  output?: string;
  userInput?: string;
  useLlmTriage?: boolean;
}): Promise<ApiResponse<FaultClassification>> {
  return apiCall("/api/v1/kernel/classify-fault", {
    method: "POST",
    body: JSON.stringify(params),
  });
}

/**
 * Diagnose which kernel in a bot's assembly is responsible for a problem
 * (raisy_61). Does NOT edit anything — pass the returned directive's
 * kernelKey + imperative_change_directive into patchKernel() (raisy_73) to
 * actually generate the edit.
 *
 * Pass `callId` to diagnose the kernels a specific call actually ran instead of the bot's
 * registered assembly. That is the ONLY way to reach a caller that has no registered
 * assembly — the kernel/schema minters build theirs per dimension, so their `bots` row is
 * an empty placeholder — and it is the accurate reading for any historical call whose bot
 * has been re-versioned since.
 */
export async function strategizeBotKernel(
  botId: string,
  instructions: string,
  last_input?: string,
  last_output?: string,
  model?: string,
  thinking_level?: string,
  callId?: string | null,
): Promise<ApiResponse<StrategizeResponse>> {
  return apiCall("/api/v1/kernel/bot_2_61_strategize", {
    method: "POST",
    body: JSON.stringify({ botId, instructions, last_input, last_output, model, thinking_level, callId }),
  });
}

// ── Local UV skill execution (run-traced) ────────────────────────────────────

export interface SkillRunRequest {
  skill_name: string;
  query: string;
  force_setup?: boolean;
  script_name?: string;
  timeout?: number;
  env?: Record<string, string>;
}

export interface SetupStageTrace {
  status: "skipped" | "generated" | "error";
  entrypoint_path: string;
  entry_code: string;
  dependencies: string[];
  env_vars: string[];
  llm_called: boolean;
  duration_ms: number;
  error?: string | null;
}

export interface BuildStageTrace {
  status: "ok" | "already_exists" | "error";
  venv_path: string;
  python_path: string;
  duration_ms: number;
  error?: string | null;
}

export interface RunStageTrace {
  status: "ok" | "error";
  script_path: string;
  input_raw: string;
  input_parsed?: Record<string, unknown> | null;
  output_raw: string;
  output_parsed?: Record<string, unknown> | null;
  duration_ms: number;
  error?: string | null;
}

export interface SkillRunTrace {
  skill_name: string;
  setup: SetupStageTrace;
  build: BuildStageTrace;
  run: RunStageTrace;
  overall_status: "ok" | "error";
  total_duration_ms: number;
  timestamp: string;
}

/** Execute a local UV skill and return the full setup → build → run trace. */
export async function runSkillTraced(
  data: SkillRunRequest,
): Promise<SkillRunTrace> {
  const headers = await AuthUtils.getAuthHeaders();
  const res = await fetch(`${API_URL}/api/v1/skill-run/traced`, {
    method: "POST",
    headers: { ...headers, "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    throw new Error(`runSkillTraced failed: ${res.status}`);
  }
  return res.json();
}

// ── T0413 — run history ───────────────────────────────────────────────────────
//
// The DURABLE record of what was run, replacing this browser's `localStorage` note as the
// thing the form preloads from. `SkillRunModal` still writes that note at dispatch (it costs
// nothing and covers the case where this read fails), but it is a convenience: it does not
// survive a different browser, and it is not a record anyone can audit.
//
// `input` is the whole point of the entry — it is the exact string that was sent, which is
// what a re-run preloads. `output` is CLIPPED by the BE and `outputTruncated` says so, so a
// snippet is never read as the whole output.

export interface SkillRunHistoryEntry {
  callId: string;
  /** Version-STABLE skill key — every run of this skill, across revisions. */
  skillKey: string | null;
  /** Version-PINNED kernel id. Answers "which exact artefact ran", not "all runs". */
  skillId: string | null;
  skillVersion: number | null;
  scriptName: string | null;
  /** The input as sent. This is what a re-run loads. */
  input: string | null;
  status: string;
  overallStatus: "ok" | "error" | null;
  runStatus: "ok" | "error" | null;
  durationMs: number | null;
  timestamp: string | null;
  userId: string | null;
  entityId: string | null;
  output: string | null;
  outputTruncated: boolean | null;
  error: string | null;
}

export interface SkillRunHistory {
  runs: SkillRunHistoryEntry[];
  pagination: { page: number; pageSize: number; total: number; pages: number };
  /** The CALLER'S OWN most recent input for `skillKey` — present only when it was given. */
  lastInput?: string | null;
}

export interface SkillRunHistoryRequest {
  skill_key?: string;
  skill_id?: string;
  page?: number;
  pageSize?: number;
}

/**
 * Past runs of a skill, newest first.
 *
 * Scoped by the BE to what the caller may read: runs of entities they can open, plus their
 * own runs. Every BD155 skill kernel in the deployment is global (`entity_id = NULL`), so
 * the second branch is what makes anything appear here at all — an empty list is a normal
 * answer for a skill nobody has run, not an error.
 */
export async function listSkillRuns(
  data: SkillRunHistoryRequest,
): Promise<SkillRunHistory> {
  const headers = await AuthUtils.getAuthHeaders();
  const res = await fetch(`${API_URL}/api/v1/skill-run/history`, {
    method: "POST",
    headers: { ...headers, "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    throw new Error(`listSkillRuns failed: ${res.status}`);
  }
  const body = await res.json();
  // The BE answers in the house envelope ({status, statusCode, message, data}).
  return (body?.data ?? body) as SkillRunHistory;
}

/** @deprecated Use editBot() instead — creates new version. */
export async function updateBotKernels(
  botId: string,
  kernel_keys_ref: string[],
): Promise<ApiResponse> {
  return apiCall("/api/v1/bot/update_kernels", {
    method: "POST",
    body: JSON.stringify({ botId, kernel_keys_ref }),
  });
}

/**
 * T0120: re-attempt resolution of a bot's pending BD155 skill slot(s) — the same
 * healer the construct engine runs automatically when an incomplete bot is reused.
 * No-op (still status:true) if the bot has nothing pending.
 */
export async function retryBotSkills(
  botId: string,
): Promise<ApiResponse<{ botId: string; kernel_keys_ref: string[]; pendingSkills: PendingSkill[] | null }>> {
  return apiCall(`/api/v1/bot/${encodeURIComponent(botId)}/retry-skills`, {
    method: "POST",
  });
}

/**
 * Lifecycle stage of each unresolved BD155 slot on a bot.
 *
 * `pendingSkills` on the registry only says a slot is empty. This joins each slot to its
 * dev_requests row, so the UI can say *where* the skill is — untriaged, in development,
 * ready, or rejected — instead of a flat "in development" for all four.
 */
export async function getBotSkillStatus(
  botId: string,
): Promise<ApiResponse<{ botId: string; slots: SkillSlotStatus[]; pendingCount: number; readyCount: number }>> {
  return apiCall(`/api/v1/bot/${encodeURIComponent(botId)}/skill-status`);
}

/**
 * Bind a finished BD155 kernel into a bot's assembly, filling one pending slot.
 *
 * Use this over retryBotSkills when the kernel is known: retry re-runs the acquisition
 * *search*, which can keep missing a kernel that exists (scoring below threshold is not
 * absence). `botDimensionSub` is sent so the BE can reject a cross-category bind — a 400
 * here means the kernel is filed under a different BD155.<sub> than the slot needs.
 */
export async function attachSkillKernel(
  botId: string,
  kernelKey: string,
  botDimensionSub?: number | null,
): Promise<ApiResponse<{ botId: string; kernel_keys_ref: string[]; pendingSkills: PendingSkill[] | null }>> {
  return apiCall(`/api/v1/bot/${encodeURIComponent(botId)}/attach-skill`, {
    method: "POST",
    body: JSON.stringify({ kernelKey, botDimensionSub: botDimensionSub ?? null }),
  });
}

// ── Dev Requests (admin triage queue, T0107 / PROJECT-076) ──────────
//
// dev_requests is PROJECT-058's DEV-REQUEST-001 review queue. Two BE write sites
// today: a non-service user saving a BD155 kernel directly, and
// resolve_or_acquire_skill() when no skill could be found anywhere. This file only
// reads/updates the queue — nothing here writes a new request.

export type DevRequestStatus = "open" | "in_progress" | "resolved" | "rejected";

/** The request roster's indices (PROJECT-289 D-289-005, RULES_NAMING §10). One name and one
 *  index each, keyed to the SEAT that owns the request rather than to the work — because what a
 *  reader must know before opening a row is WHO ACTS.
 *
 *  `RD` · `RO` · `RC` have no writer today; they exist so a request of that kind has a home before
 *  the first one arrives. The server is the authority on this list (it refuses an unknown value
 *  with 400 rather than returning an empty page), and this union is the FE's copy of it. */
export type DevRequestKind = "RB" | "RM" | "RD" | "RO" | "RC";

/** The printed name per kind — what a screen shows. `name` exists so a reader gets the spelled-out
 *  word the server sends; this map is the fallback for a value the server names but this build has
 *  not learned yet, which is the honest failure mode for a client older than the roster. */
export const DEV_REQUEST_KIND_LABELS: Record<DevRequestKind, string> = {
  RB: "Build Request",
  RM: "Market Request",
  RD: "Direction Request",
  RO: "Operations Request",
  RC: "Capital Request",
};

/** A remote skill candidate held for review (T0201), as recorded on the request (T0406).
 *
 * `slug` is the ONLY guaranteed key, and that is the truth rather than a partial read: the
 * approval hold runs BEFORE the download, so the artefact has not been fetched and there is no
 * version, content hash or trust signal to report yet (C7/T0205 measures those). Absent keys
 * are absent — the UI must not render an unmeasured value as a known one. */
export interface DevRequestCandidate {
  slug: string;
  version?: string;
  contentHash?: string;
  trustSignals?: Record<string, unknown>;
}

export interface DevRequestData {
  id: string;
  /** PROJECT-289 D-289-005 — which SEAT owns this request, as the roster's index:
   *  RB Build · RM Market · RD Direction · RO Operations · RC Capital. `null` means the row
   *  predates the roster ("not yet classified"), which is a different fact from "no kind" —
   *  migration 000059 leaves the column nullable on purpose. */
  kind: DevRequestKind | null;
  /** The roster's printed name (`Build Request`), derived server-side from `kind`. The index is
   *  for code; this is what a screen shows (RULES_NAMING §10 rule 3). */
  kindName: string | null;
  /** The seat that owns the kind (`CTO`, `CMO`, …), also derived server-side. */
  kindSeat: string | null;
  /** Kind-specific data, one object per kind. Optional because most kinds carry none. */
  payload?: Record<string, unknown> | null;
  /** PROJECT-289 T0289.3 — who CLOSED the row. Written since PROJECT-076e and serialized by
   *  nobody until now, so this page could show who raised a request and never who decided it. */
  resolvedBy: string | null;
  /** PROJECT-289 T0289.21 — how many hours this request has been open, measured from when it
   *  ARRIVED (not when anyone last touched it: a row bumped every afternoon is not a row anyone
   *  is working on). ⚠️ `null` on a row that is no longer open — a resolved row's age is how long
   *  it waited for an ANSWER, and rendering that as an age would show a closed ticket as fresh. */
  openHours: number | null;
  /** The verdict, computed server-side from the same measurement so the number and the badge
   *  cannot disagree. `true` only for a row past `staleAfterHours`. */
  stale: boolean;
  /** ⚠️ NULLABLE since PROJECT-289 D-289-001 (migration 000059). It was `NOT NULL`, which is what
   *  made a non-skill request impossible to insert — so a row may legitimately have no skill_spec,
   *  and any `.toLowerCase()` on it is a crash waiting for the first such row. */
  skillSpec: string | null;
  /** ⚠️ NULLABLE for the same reason: a request raised by a PERSON or a SURFACE has no bot, and
   *  inventing one to satisfy the old constraint is the alternative that change refuses. */
  requestingBot: string | null;
  requestingEntity: string | null;
  sourceNode: string | null;
  proposalId: string | null;
  status: DevRequestStatus;
  resolutionNotes: string | null;
  /** What this request is about. Empty for a not-found request — nothing was held, which is a
   *  different fact from "held and the list was lost". */
  candidates: DevRequestCandidate[];
  createdAt: string | null;
  updatedAt: string | null;
  resolvedAt: string | null;
}

export interface ListDevRequestsParams {
  page?: number;
  pageSize?: number;
  /** Blank = all statuses. */
  status?: DevRequestStatus | "";
  /** PROJECT-289 T0289.3 — blank = every kind. The server REFUSES an unknown value (400) rather
   *  than returning an empty list, so a typo cannot be mistaken for "the queue is empty". */
  kind?: DevRequestKind | "";
}

export async function listDevRequests(
  params?: ListDevRequestsParams,
): Promise<ApiResponse<{ requests: DevRequestData[]; total: number; page: number; pageSize: number; totalPages: number; /** PROJECT-289 T0289.21 — the declared threshold the rows' `stale` flags were computed against, sent so no client restates the number. */ staleAfterHours: number }>> {
  const p = new URLSearchParams();
  p.set("page", String(params?.page ?? 1));
  p.set("pageSize", String(params?.pageSize ?? 20));
  if (params?.status) p.set("status", params.status);
  if (params?.kind) p.set("kind", params.kind);
  return apiCall(`/api/v1/dev-requests/list?${p.toString()}`);
}

/** One request as ITS REQUESTER reads it — PROJECT-289 T0289.26.
 *
 * ⚠️ **A DELIBERATELY SMALLER SHAPE THAN `DevRequestData`, AND THE DIFFERENCE IS THE FEATURE.**
 * The requester's route (`/mine`) omits the reviewer's prose (`resolutionNotes`), the held
 * third-party candidates, the kind-specific `payload`, `proposalId` and `resolvedBy` — so this
 * type does not extend `DevRequestData` and must not be widened into it. What it keeps is the
 * whole answer to *"what happened to my ask"*: what it is, where it went, which state it is in,
 * and when it moved. `status` is the storage encoding; the requester's WORDS come from
 * `DEV_REQUEST_STAGE` in `botSkillPresentation.ts` — the FE's single mapping for the four storage
 * values, which is why no second vocabulary is minted here. */
export interface MyRequestData {
  id: string;
  kind: DevRequestKind | null;
  kindName: string | null;
  status: DevRequestStatus;
  createdAt: string | null;
  updatedAt: string | null;
  /** Set only once the row reached a final state, so a requester can see WHEN it was answered
   *  without being told who answered it. */
  resolvedAt: string | null;
}

/** The caller's OWN requests. Takes **no scoping parameter** — the server derives the scope from
 *  the token, so there is nothing here a caller could vary to reach another person's rows
 *  (`@P289` T0289.26/T0289.27: the read path was once authenticated but not authorized). */
export async function listMyRequests(
  params?: { page?: number; pageSize?: number },
): Promise<ApiResponse<{ requests: MyRequestData[]; total: number; page: number; pageSize: number; totalPages: number }>> {
  const p = new URLSearchParams();
  p.set("page", String(params?.page ?? 1));
  p.set("pageSize", String(params?.pageSize ?? 20));
  return apiCall(`/api/v1/dev-requests/mine?${p.toString()}`);
}

/** One non-owned kind's totals on the seat board — a NUMBER, never its rows.
 *
 * PROJECT-289 T0289.18: a seat sees what it must act on, and is TOLD the rest exists without being
 * made to read it. The name and the owning seat travel with the count so the screen can say
 * *"3 Market Requests (CMO)"* rather than printing a bare index. */
export interface SeatBoardCountedKind {
  kind: DevRequestKind | null;
  kindName: string | null;
  seat: string | null;
  total: number;
  /** How many of them are still somebody's work — the number the seat is owed, since a backlog of
   *  closed rows is not a backlog. */
  open: number;
}

/** A seat's view: "needs me" separated from "counted" (PROJECT-289 T0289.18/T0289.21).
 *
 * ⚠️ THE SEPARATION IS THE BACKEND'S, NOT THIS CLIENT'S. `needsMe.requests` contains **zero** rows
 * of a kind the seat does not own — the server filters, so hiding is never this file's job. Do not
 * "helpfully" merge `counted` into the table; that would undo the distinction the route exists to
 * draw. */
export interface SeatBoardData {
  seat: string;
  /** The kinds this seat owns, as the roster's indices. */
  kinds: DevRequestKind[];
  /** The DECLARED threshold the `stale` flag is computed from, sent with the rows so the screen can
   *  print the number beside a badge. A stale badge with no number beside it is the
   *  signal-without-a-number the rules forbid, because a reader cannot tell 4 days from 40. */
  staleAfterHours: number;
  needsMe: {
    requests: DevRequestData[];
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
    /** ⚠️ These two count THIS PAGE's rows, not the queue — the names say so on purpose. */
    openOnPage: number;
    staleOnPage: number;
  };
  counted: SeatBoardCountedKind[];
}

/** The five seats, in roster order. Mirrors `request_kinds.SEATS` server-side; the server refuses
 *  an unknown seat with 400 rather than answering for one nobody named. */
export const REQUEST_SEATS = ["CTO", "CMO", "CEO", "COO", "CFO"] as const;
export type RequestSeat = (typeof REQUEST_SEATS)[number];

/** Fetch one seat's board. `seat` is required — there is no user→seat binding in the estate, so
 *  the seat cannot be derived from the token and is asked for explicitly instead. */
export async function fetchSeatBoard(
  seat: RequestSeat,
  params?: { page?: number; pageSize?: number },
): Promise<ApiResponse<SeatBoardData>> {
  const p = new URLSearchParams();
  p.set("seat", seat);
  p.set("page", String(params?.page ?? 1));
  p.set("pageSize", String(params?.pageSize ?? 20));
  return apiCall(`/api/v1/dev-requests/board?${p.toString()}`);
}

/** What the resolve route reports back about the approvals it wrote (T0406 / KF-34).
 *
 * `failed` exists because `record_approval` never raises — by design, so a failed audit write
 * cannot fail the resolve. Without this the caller would get `status: true` for an approval that
 * does not exist, which is the failure mode KF-30 was about, one layer up. */
export interface DevRequestApprovalReport {
  recorded: string[];
  failed: string[];
}

/** Mark a dev request resolved/rejected/in_progress. Never transitions back to 'open' here.
 *
 * `approved_slugs` names the skills this resolution approves (T0406 / KF-34). It replaces the
 * hand-typed `APPROVED-SLUG:<slug>` marker the FE never knew about and that CONTRACT deletes:
 * without it, after that deploy an admin's only approve action would return 200 and record
 * nothing. The marker is still accepted server-side during EXPAND, so older clients keep
 * working. */
export async function resolveDevRequest(
  id: string,
  update: {
    status: Exclude<DevRequestStatus, "open">;
    resolution_notes?: string;
    approved_slugs?: string[];
  },
): Promise<ApiResponse<DevRequestData & { approvals?: DevRequestApprovalReport }>> {
  return apiCall(`/api/v1/dev-requests/${id}/resolve`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(update),
  });
}

/** One live approval for a slot, as `GET /skill-approvals/list` reports it (D-098-022).
 *
 * This is the record a reviewer has to be able to LOCATE in order to withdraw it — an approval
 * that cannot be found cannot be corrected. Snake_case throughout because the route returns
 * `approval_state`'s dict unchanged, and renaming the fields here would put a second spelling
 * of the same fact on the wire. */
export interface LiveSkillApproval {
  id: string;
  slug: string;
  source_node: string;
  entity_id: string | null;
  version: string | null;
  content_hash: string | null;
  kernel_key: string | null;
  approved_by: string;
  approved_at: string | null;
  notes: string | null;
}

/** What `POST /skill-approvals/revoke` took away.
 *
 * `priorApproval` is the row as it stood BEFORE the withdrawal — the route reads that first
 * because afterwards the same read returns null, so this is the only copy of what was lost.
 * `revoked: 0` accompanies the two refusals, never a success. */
export interface RevokeApprovalResult {
  slug: string;
  sourceNode: string;
  entityId: string | null;
  revoked: number;
  revokedBy: string;
  priorApproval: LiveSkillApproval | null;
}

/** What is approved for one slot right now — the lookup a withdrawal starts from.
 *
 * ⚠ A failure here must NOT be rendered as an empty list. The BE answers 500 rather than "none
 * are approved" when the table cannot be read, because to a human the two answers are identical
 * and the wrong one invites them to approve what is already approved. Same reasoning one layer
 * up: an unreadable list is not an empty list. */
export async function listSkillApprovals(params: {
  sourceNode: string;
  entityId?: string | null;
}): Promise<
  ApiResponse<{ sourceNode: string; entityId: string | null; approvals: LiveSkillApproval[] }>
> {
  const p = new URLSearchParams();
  p.set("source_node", params.sourceNode);
  // Omitted means the entity-agnostic scope. Sending an empty string would be a third scope
  // that no approval is ever written into.
  if (params.entityId) p.set("entity_id", params.entityId);
  return apiCall(`/api/v1/skill-approvals/list?${p.toString()}`);
}

/** Withdraw an approval, attributed to the caller (D-098-022's other direction).
 *
 * `entity_id` must be the SAME scope the approval was written in. The approve route records
 * `dr.requesting_entity` as the approval's entity, so this takes the request's own value rather
 * than guessing — a mismatched scope finds nothing and would report a withdrawal that never
 * happened.
 *
 * Four answers, and only the first is a success: **200** withdrew it (and unbound every bot
 * holding the kernel); **404** nothing live matched, so nothing was withdrawn; **409** it IS
 * approved and was NOT withdrawn, because the unbind sweep failed and the withdrawal was
 * refused rather than half-recorded. All three arrive in the envelope, so the caller must read
 * `status` and `statusCode` — the transport is a 200 for every one of them. */
export async function revokeSkillApproval(body: {
  slug: string;
  source_node: string;
  entity_id?: string | null;
  reason?: string;
}): Promise<ApiResponse<RevokeApprovalResult>> {
  return apiCall("/api/v1/skill-approvals/revoke", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}
